import "../datasets/dataset-style.scss";
import "./bucket-style.scss";
import { compareArraysOfObjects } from "src/helpers/helperFunctions";

import * as d3 from "d3";
import {
  CBadge,
  CButton,
  CButtonGroup,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CModal,
  CModalBody,
  CModalHeader,
  CRow,
  CSelect,
} from "@coreui/react";
import { NucToast } from "src/reusable/components/Toast";
import toast, { Toaster } from "react-hot-toast";
import {
  PCT_DIFF_FROM_AVG_SLICE_TEXT,
  PCT_DIFF_FROM_AVG_TIME_TEXT,
  PCT_DIFF_FROM_BENCHMARK_TEXT,
} from "./BucketVisConstants";
import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import { useBucketList, useGetBucket, useUpdateBucket } from "./BucketServices";
import {
  useGetAnalytic,
  useLabelMap,
  useStatsRecordInfo,
  useUpsertAnalytic,
  useGetAnalyticResults,
} from "../datasets/services/DatasetServices";
import { useHistory } from "react-router-dom";
// react-autocomplete is unmaintained upstream; replace when this view is next reworked.
import Autocomplete from "react-autocomplete";
import { BsBucketFill } from "react-icons/bs";
import { BucketListModal } from "./Buckets";
import BucketVisOptions from "./BucketVisOptions";
import CIcon from "@coreui/icons-react";
import { CreateBucketModal } from "../forms/BucketModals";
import { DatasetModelStatsResultsTable } from "../datasets/DatasetModelStats";
import ErrorMessageAlerts from "src/reusable/ErrorMessageAlerts";
import { Loading } from "src/reusable/LoadingSpinner";
import { MultiDetailGroup } from "../products/cap-ai/cap-automation/automation-records/AutomationRecords";
import PropTypes from "prop-types";
import ReactDOMServer from "react-dom/server";
import { freeSet } from "@coreui/icons";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const blendRGBAArrays = (color1, color2, ratio) => {
  // function for blending two colors
  let newColor = [];
  for (let i = 0; i < 4; i++) {
    newColor.push(color1[i] * (1 - ratio) + color2[i] * ratio);
  }
  return newColor;
};

const colorFunc = (d, colorScaleData, selectedLabelSet) => {
  // function for dynamically calculating the color of labels
  // args:
  //  d: the data point
  //  colorScaleData: the data used to scale the colors.  Example:
  //    [
  //      [-1.0, [0, 0, 255, 0.8]],
  //      [0.0, [128, 128, 128, 0.8]],
  //      [1.0, [255, 0, 0, 0.8]]
  //    ]
  if (!selectedLabelSet) {
    selectedLabelSet = new Set();
  }

  const heat = d.heat;

  let heatColor = colorScaleData[0][1]; // default to low min
  for (let i = 1; i < colorScaleData.length; i++) {
    // look at every upcoming color, if the heat is between the current and next, use the next color
    // TODO: current operation is O(N), could be optimized with binary search to make O(log(N)) if large number of colors provided
    if (heat <= colorScaleData[i][0]) {
      let [prevHeatThresh, prevHeatColor] = colorScaleData[i - 1];
      let [nextHeatThresh, nextHeatColor] = colorScaleData[i];
      const intraThresholdRange =
        (heat - prevHeatThresh) / (nextHeatThresh - prevHeatThresh);

      heatColor = blendRGBAArrays(
        prevHeatColor,
        nextHeatColor,
        intraThresholdRange,
      );

      break;
    } else {
      heatColor = colorScaleData[i][1];
    }
  }

  let opacity = heatColor[3];
  // if there are selected points, make other points more opaque and the selected points less
  if (selectedLabelSet.size > 0) {
    // TODO: make the distinction more apparent.  Maybe a highlight?
    if (selectedLabelSet.has(d.Label)) {
      opacity = 1.0;
    } else {
      opacity *= 0.5;
    }
  }
  return `rgba(${heatColor[0]}, ${heatColor[1]}, ${heatColor[2]}, ${opacity})`;
};

const calcResultColumns = (analytic) => {
  const bucketColumn = analytic.model_label_class.model_output_name;
  let sliceByColumn = null;
  if (analytic.config.slice_by.length > 0) {
    sliceByColumn = analytic.config.slice_by[0].name + " (slice)";
  }
  let timeSliceColumn = null;
  if (analytic.config.time_slice) {
    timeSliceColumn = analytic.config.time_slice.field.name + " (time slice)";
  }
  let benchmarkColumn = null;
  if (analytic.config.benchmark) {
    benchmarkColumn =
      analytic.config.benchmark.benchmark_field + " (benchmark)";
  }
  return {
    bucketColumn,
    sliceByColumn,
    timeSliceColumn,
    benchmarkColumn,
  };
};

const calcStats = (data) => {
  // calc some useful stats for later usage
  const averageCount = d3.mean(data, (d) => d.count);
  const maxCount = d3.max(data, (d) => d.count);
  // calculate the range of the x and y axes
  let xRange = [d3.min(data, (d) => d.umap_x), d3.max(data, (d) => d.umap_x)];
  xRange[0] -= (xRange[1] - xRange[0]) * 0.05;
  xRange[1] += (xRange[1] - xRange[0]) * 0.05;
  let yRange = [d3.min(data, (d) => d.umap_y), d3.max(data, (d) => d.umap_y)];
  yRange[0] -= (yRange[1] - yRange[0]) * 0.05;
  yRange[1] += (yRange[1] - yRange[0]) * 0.05;
  const minHeat = d3.min(data, (d) => d.heat);
  const maxHeat = 0.1; //d3.max(data, (d) => d.heat);
  return {
    averageCount: averageCount,
    maxCount: maxCount,
    xRange,
    yRange,
    minHeat,
    maxHeat,
  };
};

const LabelToolTip = ({ dataPoint, bucketVisParams }) => {
  /* Component renders the contents of a tool tip for a given label inside the
     analytic.  Includes the POC, the description, heat, and heat calculation
     details */
  // Style for the table cells
  const tdStyle = {
    borderCollapse: "collapse",
    border: "1px solid",
    padding: "1px 5px",
  };
  // Reformat the heatWeights so they are easier to lookup by the datapoint
  // heatContributor keys
  const weightCalcLookup = {
    [PCT_DIFF_FROM_AVG_SLICE_TEXT]: bucketVisParams.heatWeights.slice,
    [PCT_DIFF_FROM_AVG_TIME_TEXT]: bucketVisParams.heatWeights.time,
    [PCT_DIFF_FROM_BENCHMARK_TEXT]: bucketVisParams.heatWeights.benchmark,
  };
  // The heatWeights are raw weights, not turned into "proportions".  Take the
  // sum of applicable weights to be used to convert into percentage proportions
  const weightCalcDenom = Object.keys(dataPoint.heatContributors)
    .map((hcKey) => weightCalcLookup[hcKey])
    .reduce((partialSum, a) => partialSum + a, 0);
  return (
    <div>
      <b>{dataPoint.Label}</b>
      {dataPoint?.Description && (
        <>
          <b>: </b>
          {dataPoint?.Description}
          <br />
        </>
      )}
      <br />
      <b>% of Records:</b> {(100 * dataPoint.proportion).toFixed(3)}
      %&nbsp;&nbsp;&nbsp;
      <b>Record Count:</b> {dataPoint.count}
      {dataPoint.benchmarkProportion && (
        <>
          <br />
          <b>Benchmark % of Records:</b>{" "}
          {(100 * dataPoint.benchmarkProportion).toFixed(3)}%
        </>
      )}
      <br />
      <b>Heat:</b> {(100 * dataPoint.heat).toFixed(3)}%&nbsp;
      <span /* A little box to display the heat color */
        style={{
          backgroundColor: colorFunc(
            dataPoint,
            bucketVisParams.heatColors,
            new Set([dataPoint.Label]),
          ),
        }}
      >
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
      </span>
      <table /* Render a table of everything that goes into the heat calculation */
        style={{
          borderCollapse: "collapse",
          border: "1px solid",
        }}
      >
        <thead>
          <tr>
            <th style={tdStyle}>Metric</th>
            <th style={tdStyle}>Value</th>
            {/* Only include weight if the bucketVisParams are provided */}
            {bucketVisParams && <th style={tdStyle}>Weight</th>}
          </tr>
        </thead>
        <tbody>
          {Object.entries(dataPoint.heatContributors).map((item) => {
            const [key, value] = item;
            return (
              <tr key={key}>
                <td style={tdStyle}>{key}</td>
                <td style={tdStyle}>{(100 * value).toFixed(3)}%</td>
                {bucketVisParams && (
                  <td style={tdStyle}>
                    {((100 * weightCalcLookup[key]) / weightCalcDenom).toFixed(
                      1,
                    )}
                    %
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

LabelToolTip.propTypes = {
  dataPoint: PropTypes.shape({
    Label: PropTypes.string.isRequired,
    Description: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    proportion: PropTypes.number.isRequired,
    benchmarkProportion: PropTypes.number,
    heat: PropTypes.number.isRequired,
    heatContributors: PropTypes.object.isRequired,
  }).isRequired,
  bucketVisParams: PropTypes.object,
};

const BucketTools = ({ onSearchTextChange, onResetZoom }) => {
  const [searchText, setSearchText] = useState("");
  const [searchEnabled, setSearchEnabled] = useState(false);
  const searchInputRef = useRef(null);
  useEffect(() => {
    // If search input is enabled, focus on text input
    if (searchEnabled) {
      searchInputRef.current.focus();
    }
  }, [searchEnabled]);
  useEffect(() => {
    //When search text changes, call onSearchTextChange
    onSearchTextChange(searchText);
  }, [searchText]);
  return (
    <CButtonGroup>
      {!searchEnabled && (
        <>
          <CButton
            size="sm"
            data-attr="buckets:btn:open-search"
            onClick={() => {
              setSearchEnabled(true);
              setSearchText("");
            }}
          >
            <CIcon content={freeSet.cilMagnifyingGlass} />
          </CButton>
          <CButton
            id="resetZoom"
            size="sm"
            onClick={onResetZoom}
            data-attr="buckets:btn:reset-zoom"
          >
            <CIcon content={freeSet.cilResizeBoth} />
          </CButton>
        </>
      )}
      {searchEnabled && (
        <>
          <input
            type="text"
            ref={searchInputRef}
            data-attr="buckets:input:search-labels"
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            value={searchText}
            style={{
              fontSize: "85%",
            }}
          ></input>
          <CButton
            id="clearSearch"
            size="sm"
            data-attr="buckets:btn:clear-search"
            onClick={() => {
              setSearchEnabled(false);
              setSearchText("");
            }}
          >
            <CIcon content={freeSet.cilX} />
          </CButton>
        </>
      )}
    </CButtonGroup>
  );
};

BucketTools.propTypes = {
  onSearchTextChange: PropTypes.func,
  onResetZoom: PropTypes.func,
};

BucketTools.defaultProps = {
  onSearchTextChange: () => {},
  onResetZoom: () => {},
};

const BucketVisualization = ({
  width,
  height,
  data,
  dataStats,
  onSelectedLabelsChange,
  selectedLabels,
  bucketVisParams,
  defaultBucketId,
}) => {
  const [selectedLabelSet, setSelectedLabels] = useState(selectedLabels);
  const [lastZoomEvent, setLastZoomEvent] = useState(null);
  const [searchText, setSearchText] = useState("");

  const plotRef = useRef(null);

  var zoom = d3.zoom().on("zoom", setLastZoomEvent);

  useEffect(() => {
    let eqSet =
      selectedLabelSet.size === selectedLabels.size &&
      [...selectedLabelSet].every((x) => selectedLabels.has(x));

    if (!eqSet) {
      setSelectedLabels(selectedLabels);
    }
  }, [selectedLabels]);

  useEffect(() => {
    onSelectedLabelsChange(selectedLabelSet);
  }, [selectedLabelSet, onSelectedLabelsChange]);

  /* Point size parameters */
  const POINT_SIZE_BASE = width * 0.002; // Base point size factor.  Increase to make points bigger.
  const MIN_POINT_SIZE = 0.5; // Minimum point size factor.
  const MAX_ALLOWED_POINT_SIZE = width * 0.035; // Maximum allowed point size factor.  If zoomed visualizations are too cramped, decrease this value.
  const MIN_MAX_POINT_SIZE = width * 0.01; // The largest point size should be at least this size.

  /* Font and label parameters */
  const LABEL_FONT_SIZE = 12.0; // Font size for labels
  const MIN_K_LABEL_DISPLAY = 4.0;
  const MIN_K_LABEL_DISPLAY_0_OPACITY = 10.0;

  const pointSizeFunc = (d, avgCount, zoomFactor, maxCount) => {
    // function for dynamically calculating the size of circles
    // args:
    //  d: the data point
    //  avgCount: the average count values for the visible data
    //  zoomFactor: the zoom factor
    //  maxCount: the maximum count value for the visible data

    // First, calculate the desired point size based on the count of the data point (normalize based on the avgCount)
    let pointSizePlus = POINT_SIZE_BASE * Math.sqrt(d.count / avgCount);
    if (maxCount > 0) {
      // If a maxCount is provided, calculate the size the max count would be
      const zoomedMaxPointSize =
        POINT_SIZE_BASE * Math.sqrt(maxCount / avgCount) * zoomFactor;
      if (zoomedMaxPointSize > MAX_ALLOWED_POINT_SIZE) {
        // If the largest visible point is above the max allowed size, we need to scale all points proportionally to ensure the largest point is not too big
        const downRatio = MAX_ALLOWED_POINT_SIZE / zoomedMaxPointSize;
        pointSizePlus *= downRatio;
      } else if (zoomedMaxPointSize < MIN_MAX_POINT_SIZE) {
        // If the largest visible point is below the min allowed size, we need to scale all points proportionally to ensure the largest point is not too small
        const upRatio = MIN_MAX_POINT_SIZE / zoomedMaxPointSize;
        pointSizePlus *= upRatio;
      }
    }
    // Add the minimum point size.  Min point size will increase a bit as the zoom factor increases.
    const returnVal = Math.sqrt(MIN_POINT_SIZE / zoomFactor) + pointSizePlus;
    if (isNaN(returnVal)) {
      return 1.0;
    }
    return returnVal;
  };

  const fontOpacityFunc = (d, zoomFactor) => {
    // function for dynamically calculating the opacity of labels
    // args:
    //  d: the data point
    //  zoomFactor: the current zoom factor
    if (zoomFactor < MIN_K_LABEL_DISPLAY) {
      // If the current zoom factor is less than the max allowed, make transparent
      return 0.0;
    } else if (zoomFactor >= MIN_K_LABEL_DISPLAY_0_OPACITY) {
      // If the current zoom factor is greater than the 0 opacity max, have no transparency
      return 1.0;
    } else {
      // Otherwise, calculate the opacity based on the current zoom factor
      // Scale opacity from 0.25 to 1 based on the current zoom factor
      // Quadratic scale seemed to work well
      // 0.25 is min opacity, below 0.25 the visual looked bad/weird
      return (
        0.25 +
        0.75 *
          ((zoomFactor - MIN_K_LABEL_DISPLAY) /
            (MIN_K_LABEL_DISPLAY_0_OPACITY - MIN_K_LABEL_DISPLAY)) **
            2
      );
    }
  };

  const fontSizeFunc = (d, zoomFactor) => {
    // function for dynamically calculating the size of labels
    return LABEL_FONT_SIZE / zoomFactor;
  };

  const shouldSearchHighlight = (d) => {
    return (
      searchText !== "" &&
      (d?.Label?.toLowerCase()?.includes(searchText) ||
        d?.Description?.toLowerCase()?.includes(searchText))
    );
  };

  const strokeColor = (d) => {
    if (shouldSearchHighlight(d)) {
      return "#00B000";
    }
  };

  const strokeWidth = (d, zoomFactor) => {
    if (shouldSearchHighlight(d)) {
      return 8 / zoomFactor;
    }
  };

  const datapointVisible = (d, xDomain, yDomain) => {
    // function for checking if a data point is in the x and y domains
    return (
      d.umap_x > xDomain[0] &&
      d.umap_x < xDomain[1] &&
      d.umap_y > yDomain[0] &&
      d.umap_y < yDomain[1]
    );
  };

  const handleZoomQuick = (zoomEvent) => {
    // non-debounced zoom handling, renders quickly
    if (zoomEvent && dataStats) {
      // zoom event handler
      // update the transform on the svg element
      var svg = d3.select(plotRef.current).select("g");
      svg.attr("transform", zoomEvent.transform);
    }
  };

  const handleZoomSlow = (zoomEvent) => {
    if (zoomEvent && dataStats) {
      var x = d3.scaleLinear().domain(dataStats.xRange).range([0, width]);
      var y = d3.scaleLinear().domain(dataStats.yRange).range([height, 0]);
      // get the new domains (x and y) after the zoom
      const xDomain = zoomEvent.transform.rescaleX(x).domain();
      const yDomain = zoomEvent.transform.rescaleY(y).domain();
      // calculate new stats for the visible data points
      let insideDomainCount = 0;
      let totalCount = 0;
      let maxCount = 0.0;
      d3.selectAll(".dot").each(function (d) {
        if (datapointVisible(d, xDomain, yDomain)) {
          insideDomainCount++;
          totalCount += d.count;
          if (d.count > maxCount) {
            maxCount = d.count;
          }
        }
      });
      // Based on the new stats, update the circles and the labels accordingly
      d3.selectAll(".dot")
        .transition()
        .duration(500)
        .attr("r", (d) =>
          pointSizeFunc(
            d,
            totalCount / insideDomainCount,
            zoomEvent.transform.k,
            maxCount,
          ),
        )
        .style("stroke-width", (d) => strokeWidth(d, zoomEvent.transform.k))
        .style(
          "display",
          (d) => (datapointVisible(d, xDomain, yDomain) ? "inline" : "none"), // hide the dots that are not in the domain
        );
      d3.selectAll(".dot-label")
        .transition()
        .duration(500)
        .attr("font-size", (d) => fontSizeFunc(d, zoomEvent.transform.k))
        .style("opacity", (d) => fontOpacityFunc(d, zoomEvent.transform.k));
    }
  };

  const handleZoom = (zoomEvent) => {
    handleZoomQuick(zoomEvent);
    handleZoomSlow(zoomEvent);
  };

  // zoom events can happen like crazy (i think one event per pixel moved in both x and y for a drag?) so debounce them to prevent too many events from being fired.
  const debouncedLastZoomEvent = useDebounce(lastZoomEvent, 10);
  useEffect(() => {
    handleZoomQuick(lastZoomEvent);
  }, [lastZoomEvent, dataStats]);
  useEffect(() => {
    handleZoomSlow(debouncedLastZoomEvent);
  }, [debouncedLastZoomEvent, dataStats]);

  const renderPlot = () => {
    // clear out everything in the current plotRef
    d3.select(plotRef.current).selectAll("*").remove();
    // append the svg object to the body of the page
    var svg = d3
      .select(plotRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .append("g");
    // Setup x scale and x axis
    var x = d3.scaleLinear().domain(dataStats.xRange).range([0, width]);
    var xAxis = d3.axisBottom(x).tickValues([]);
    svg
      .append("g")
      .attr("transform", "translate(0," + height + ")")
      .call(xAxis);
    // Add y scale and y axis
    var y = d3.scaleLinear().domain(dataStats.yRange).range([height, 0]);
    var yAxis = d3.axisLeft(y).tickValues([]);
    svg.append("g").call(yAxis);
    svg.selectAll("path").remove(); // removes the axis lines
    d3.select(plotRef.current).call(zoom); // attach the zoom event handler to the plotRef
    // tooltip
    var tooltip = d3
      .select(plotRef.current)
      .append("div")
      .style("opacity", 0.0)
      .attr("class", "tooltip")
      .style("background-color", "rgba(255,255,255,0.9)")
      .style("border", "solid")
      .style("border-width", "1px")
      .style("border-radius", "5px")
      .style("padding", "10px");

    var mouseClick = (d) => {
      // mouse click event handler
      let newLabels = [];
      const label = d.target.__data__.Label;
      // have to build a new set each time.  selectedLabels is a const, so can't add or remove from it by reference
      if (selectedLabelSet.has(label)) {
        newLabels = Array.from(selectedLabelSet).filter((lbl) => lbl !== label);
      } else {
        newLabels = Array.from(selectedLabelSet).concat(label);
      }
      setSelectedLabels(new Set(newLabels));
      //renderPlot();
    };
    var mouseOver = function () {
      tooltip.style("opacity", 1.0); // render the tooltip
    };
    var mouseMove = function (mouseEvent) {
      // render the tooltip based on the mouse position
      tooltip
        .html(
          ReactDOMServer.renderToStaticMarkup(
            <LabelToolTip
              dataPoint={mouseEvent.target.__data__}
              bucketVisParams={bucketVisParams}
            />,
          ),
        )
        .style("left", mouseEvent.layerX + 20 + "px") // offset the tooltip to the mouse position
        .style("top", mouseEvent.layerY - 10 + "px"); // offset the tooltip to the mouse position
    };

    var mouseLeave = function () {
      // A function that change this tooltip when the leaves a point: just need to set opacity to 0 again and move it off the screen
      tooltip.style("opacity", 0).style("top", "100000px");
    };
    const transformK = lastZoomEvent ? lastZoomEvent.transform.k : 1.0;
    // add the scatter plot data
    var svgg = svg.append("g");
    // bubble group for the circle and label
    var bubbles = svgg
      .selectAll(".bubble")
      .data(data)
      .enter()
      .append("g")
      .classed("bubble", true)
      .on("click", mouseClick)
      .on("mouseover", mouseOver)
      .on("mousemove", mouseMove)
      .on("mouseleave", mouseLeave);
    //  the actual points
    bubbles
      .append("circle")
      .classed("dot", true)
      .classed("pulse", shouldSearchHighlight)
      .attr("cx", function (d) {
        return x(d.umap_x);
      })
      .attr("cy", function (d) {
        return y(d.umap_y);
      })
      .attr("r", (d) =>
        pointSizeFunc(
          d,
          dataStats.averageCount,
          transformK,
          dataStats.maxCount,
        ),
      )
      .style("fill", (d) =>
        colorFunc(d, bucketVisParams.heatColors, selectedLabelSet),
      )
      .style("stroke", strokeColor)
      .style("stroke-width", (d) => strokeWidth(d, transformK));
    // the text labels
    bubbles
      .append("text")
      .classed("dot-label", true)
      .attr("data-testid", "analytics-dot-label")
      .text(function (d) {
        return d.Label;
      })
      .attr("x", function (d) {
        return x(d.umap_x);
      })
      .attr("y", function (d) {
        return y(d.umap_y);
      })
      .attr("font-size", (d) => fontSizeFunc(d, transformK))
      .attr("fill", "rgba(0,0,0,0.5)")
      .attr("text-anchor", "middle") // center
      .attr("dy", ".35em") // center
      .style("opacity", (d) => fontOpacityFunc(d, transformK)) // # todo, handle not rerender on point select
      .style("cursor", "default");

    if (lastZoomEvent) {
      // we are re-rendering, but there is an existing zoom we should reapply}
      handleZoom(lastZoomEvent);
    }
  };

  useEffect(() => {
    if (dataStats) {
      renderPlot();
    }
  }, [
    dataStats,
    selectedLabelSet,
    searchText,
    bucketVisParams,
    defaultBucketId,
  ]);

  return (
    <div style={{ position: "relative" }}>
      <div
        id="bucket-vis-plot"
        ref={plotRef}
        style={{
          position: "relative", // Don't remove, messes with tooltips
          border: "solid",
          borderColor: "#777",
          borderWidth: "1px",
          borderRadius: "5px",
          height: height + "px",
          width: width + "px",
          backgroundColor: "#FFF",
        }}
      ></div>
      <div
        style={{ position: "absolute", top: 3, right: 3, textAlign: "right" }}
      >
        <BucketTools
          onSearchTextChange={(text) => setSearchText(text.toLowerCase())}
          onResetZoom={(e) => {
            const newZoomEvent = {
              sourceEvent: e,
              target: zoom,
              transform: d3.zoomIdentity,
              type: "zoom",
            };
            // If you don't do this, the next zoom event will increment not from the reset zoom, but the previous one
            plotRef.current.__zoom = newZoomEvent.transform;
            setLastZoomEvent(newZoomEvent);
          }}
        />
      </div>
    </div>
  );
};

BucketVisualization.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired,
  dataStats: PropTypes.shape({
    averageCount: PropTypes.number.isRequired,
    maxCount: PropTypes.number.isRequired,
    xRange: PropTypes.arrayOf(PropTypes.number).isRequired,
    yRange: PropTypes.arrayOf(PropTypes.number).isRequired,
    minHeat: PropTypes.number.isRequired,
    maxHeat: PropTypes.number.isRequired,
  }).isRequired,
  onSelectedLabelsChange: PropTypes.func.isRequired,
  selectedLabels: PropTypes.instanceOf(Set),
  bucketVisParams: PropTypes.object,
  defaultBucketId: PropTypes.string || PropTypes.number,
};

BucketVisualization.defaultProps = {
  selectedLabels: new Set(),
};

const BucketTrendChart = (props) => {
  const [trendData, setTrendData] = useState([]);
  const plotRef = useRef(null);

  useEffect(() => {
    /* Need to format the timeseries data!
  data should look like this:
    [{
      "time": "2020-01-01",
      "ER.1.01": 47,
      "NP.1.02": 24,
    }, ...]
  */
    let trendDataTracker = {};
    if (props.statsData) {
      // only build the visual data if all the required data is present
      const { bucketColumn, sliceByColumn, timeSliceColumn } =
        calcResultColumns(props.analytic);
      props.statsData.forEach((d) => {
        const count = "Count" in d ? d.Count : d["Sum of Probabilities"];
        const bucket = d[bucketColumn];
        const time = d[timeSliceColumn];
        const slice = d[sliceByColumn];
        if (
          props.selectedLabels.has(bucket) &&
          (props.bucketVisParams.crossSection === slice ||
            props.bucketVisParams.crossSection === "")
        ) {
          // check if the right slice is selected
          let timeData = trendDataTracker[time] || { time: time };
          timeData[bucket] = count;
          trendDataTracker[time] = timeData;
        }
      });
    }
    // convert the data to an array
    let trendDataKeys = Object.keys(trendDataTracker);
    trendDataKeys.sort();
    let newTrendData = [];
    trendDataKeys.forEach((key) => {
      newTrendData.push(trendDataTracker[key]);
    });
    if (compareArraysOfObjects(newTrendData, trendData)) {
      setTrendData(newTrendData);
    }
  }, [props]);

  const renderPlot = () => {
    d3.select(plotRef.current).selectAll("*").remove();
    var margin = { top: 10, right: 30, bottom: 20, left: 50 },
      width = props.width - margin.left - margin.right,
      height = props.height - margin.top - margin.bottom;
    // append the svg object to the body of the page
    var svg = d3
      .select(plotRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    var subgroups = Array.from(props.selectedLabels);
    var timeGroups = trendData.map((d) => d.time);
    // // Add X axis
    var x = d3.scaleBand().domain(timeGroups).range([0, width]).padding([0.2]);

    const maxTicks = 10;
    const everyNthTickLabel = Math.ceil(x.domain().length / maxTicks);
    svg
      .append("g")
      .attr("transform", "translate(0," + height + ")")
      .call(
        d3
          .axisBottom(x)
          .tickValues(
            x.domain().filter(function (d, i) {
              return !(i % everyNthTickLabel);
            }),
          )
          .tickSizeOuter(0),
      );
    const maxCount =
      trendData.length > 0
        ? d3.max(trendData, (d) => {
            let total = 0;
            subgroups.forEach((subgroup) => {
              const count = d[subgroup] || 0;
              total += count;
            });
            return total;
          })
        : 0;
    // Add Y axis
    var y = d3
      .scaleLinear()
      .domain([0, maxCount * 1.05])
      .range([height, 0]);
    svg.append("g").call(d3.axisLeft(y));

    svg // axis label
      .append("g")
      .attr("transform", "translate(-30, " + height / 2 + ")")
      .append("text")
      .style("font-size", "12px")
      .attr("font-style", "italic")
      .attr("text-anchor", "middle")
      .attr("fill", "black") //set the fill here
      .attr("transform", "rotate(-90)")
      .text(
        props.analytic?.config?.aggregation === "count"
          ? "Record Count"
          : "Sum of Probabilities",
      );

    // color palette = one color per subgroup
    // color palette = one color per subgroup

    let color;
    if (props.colorSelector) {
      color = props.colorSelector;
    } else {
      color = d3.scaleOrdinal().domain(subgroups).range(d3.schemeCategory10);
    }

    //stack the data? --> stack per subgroup
    var stackedData = d3.stack().keys(subgroups)(trendData);

    // Show the bars
    svg
      .append("g")
      .selectAll("g")
      // Enter in the stack data = loop key per key = group per group
      .data(stackedData)
      .enter()
      .append("g")
      .attr("fill", function (d) {
        return color(d.key);
      })
      .selectAll("rect")
      // enter a second time = loop subgroup per subgroup to add all rectangles
      .data(function (d) {
        return d;
      })
      .enter()
      .append("rect")
      .attr("x", function (d) {
        return x(d?.data?.time ? d.data.time : 0);
      })
      .attr("y", function (d) {
        return y(d[1] ? d[1] : 0);
      })
      .attr("height", function (d) {
        return y(d[0] ? d[0] : 0) - y(d[1] ? d[1] : 0);
      })
      .attr("width", x.bandwidth() ? x.bandwidth() : 0);
  };
  useEffect(() => {
    renderPlot();
  }, [trendData]);

  return (
    <LoadingOverlay
      active={props.newStatLoading}
      spinner={<Loading height={"75em"} width={"75em"} />}
      text="Updating Chart Data..."
    >
      <div id="bucket-line-chart" ref={plotRef}></div>
    </LoadingOverlay>
  );
};

BucketTrendChart.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  analytic: PropTypes.object.isRequired,
  newStatLoading: PropTypes.bool.isRequired,
  statsData: PropTypes.array.isRequired,
  selectedLabels: PropTypes.instanceOf(Set),
  bucketVisParams: PropTypes.object.isRequired,
  colorSelector: PropTypes.func,
};

const BucketComparisonChart = (props) => {
  const [comparisonData, setComparisonData] = useState([]);
  const plotRef = useRef(null);

  useEffect(() => {
    /* Need to format the timeseries data!
  data should look like this:
    [{
      "slice": "D",
      "ER.1.01": 47,
      "NP.1.02": 24,
    }, ...]
  */
    let comparisonDataTracker = {};
    if (props.statsData) {
      // only build the visual data if all the required data is present
      const { bucketColumn, sliceByColumn, timeSliceColumn } =
        calcResultColumns(props.analytic);
      props.statsData.forEach((d) => {
        const count = "Count" in d ? d.Count : d["Sum of Probabilities"];
        const bucket = d[bucketColumn];
        const time = d[timeSliceColumn];
        const slice = d[sliceByColumn];
        if (
          props.selectedLabels.has(bucket) &&
          props.bucketVisParams.time === time
        ) {
          // check if the right slice is selected

          let comparisonData = comparisonDataTracker[slice] || { slice: slice };
          comparisonData[bucket] = count;
          comparisonDataTracker[slice] = comparisonData;
        }
      });
    }
    // convert the data to an array
    let comparisonDataKeys = Object.keys(comparisonDataTracker);
    comparisonDataKeys.sort();
    let newComparisonData = [];
    comparisonDataKeys.forEach((key) => {
      newComparisonData.push(comparisonDataTracker[key]);
    });
    setComparisonData(newComparisonData);
  }, [props.statsData]);

  const renderPlot = () => {
    //TODO: proportionality options in timeslice and comparison
    d3.select(plotRef.current).selectAll("*").remove();
    var margin = { top: 10, right: 30, bottom: 50, left: 50 },
      width = props.width - margin.left - margin.right,
      height = props.height - margin.top - margin.bottom;
    // append the svg object to the body of the page
    var svg = d3
      .select(plotRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");
    var subgroups = Array.from(props.selectedLabels);
    var sliceGroups = comparisonData.map((d) => d.slice);
    // // Add X axis
    var x = d3.scaleBand().domain(sliceGroups).range([0, width]).padding([0.2]);
    svg
      .append("g")
      .attr("transform", "translate(0," + height + ")")
      .call(d3.axisBottom(x).tickSizeOuter(0));
    svg // X-axis label
      .append("g")
      .attr("transform", "translate(" + width / 2 + ", " + (height + 30) + ")")
      .append("text")
      .style("font-size", "12px")
      .attr("font-style", "italic")
      .attr("text-anchor", "middle")
      .attr("fill", "black") //set the fill here
      .text(props.analytic?.config?.slice_by?.[0]?.name);
    const maxCount =
      comparisonData.length > 0
        ? d3.max(comparisonData, (d) => {
            let total = 0;
            subgroups.forEach((subgroup) => {
              const count = d[subgroup] || 0;
              total += count;
            });
            return total;
          })
        : 0;
    // Add Y axis
    var y = d3
      .scaleLinear()
      .domain([0, maxCount * 1.05])
      .range([height, 0]);
    svg.append("g").call(d3.axisLeft(y));
    svg // Y-axis label
      .append("g")
      .attr("transform", "translate(-30, " + height / 2 + ")")
      .append("text")
      .style("font-size", "12px")
      .attr("font-style", "italic")
      .attr("text-anchor", "middle")
      .attr("fill", "black") //set the fill here
      .attr("transform", "rotate(-90)")
      .text(
        props.analytic?.config?.aggregation === "count"
          ? "Record Count"
          : "Sum of Probabilities",
      );

    // color palette = one color per subgroup

    let color;
    if (props.colorSelector) {
      color = props.colorSelector;
    } else {
      color = d3.scaleOrdinal().domain(subgroups).range(d3.schemeCategory10);
    }

    //stack the data? --> stack per subgroup
    var stackedData = d3.stack().keys(subgroups)(comparisonData);
    // Show the bars
    svg
      .append("g")
      .selectAll("g")
      // Enter in the stack data = loop key per key = group per group
      .data(stackedData)
      .enter()
      .append("g")
      .attr("fill", function (d) {
        return color(d.key);
      })
      .selectAll("rect")
      // enter a second time = loop subgroup per subgroup to add all rectangles
      .data(function (d) {
        return d;
      })
      .enter()
      .append("rect")
      .attr("x", function (d) {
        return x(d?.data?.slice ? d.data.slice : 0);
      })
      .attr("y", function (d) {
        return y(d[1] ? d[1] : 0);
      })
      .attr("height", function (d) {
        return y(d[0] ? d[0] : 0) - y(d[1] ? d[1] : 0);
      })
      .attr("width", x.bandwidth() ?? 0);
  };
  useEffect(() => {
    renderPlot();
  }, [comparisonData]);

  return (
    <LoadingOverlay
      active={props.newStatLoading}
      spinner={<Loading height={"75em"} width={"75em"} />}
      text="Updating Chart Data..."
    >
      <div id="bucket-comparison-chart" ref={plotRef}></div>
    </LoadingOverlay>
  );
};

BucketComparisonChart.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  analytic: PropTypes.object.isRequired,
  newStatLoading: PropTypes.bool.isRequired,
  statsData: PropTypes.array.isRequired,
  selectedLabels: PropTypes.instanceOf(Set),
  bucketVisParams: PropTypes.object.isRequired,
  colorSelector: PropTypes.func,
};

const BucketSourceDataRecordDisplayModal = (props) => {
  const [displayRecord, setDisplayRecord] = useState({});
  useEffect(() => {
    if (props.displayRecord) {
      setDisplayRecord(
        Object.keys(props.displayRecord).reduce((newObj, key) => {
          if (!props.hiddenKeys.includes(key)) {
            // Only add column if it is not to be skipped
            newObj[key] = props.displayRecord[key];
          }
          return newObj;
        }, {}),
      );
    } else {
      setDisplayRecord({});
    }
  }, [props.displayRecord, props.hiddenKeys]);

  return (
    <CModal
      show={props.isVisible}
      onClose={() => props.onClose()}
      style={{ textAlign: "left" }}
      size="lg"
    >
      <CModalHeader>
        <CCol sm="9">
          <h3>View Record Details</h3>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="bsdrdm-close-button"
            color="secondary"
            onClick={() => props.onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <CModalBody>
        <MultiDetailGroup objectWithKeys={displayRecord} />
      </CModalBody>
    </CModal>
  );
};

BucketSourceDataRecordDisplayModal.propTypes = {
  displayRecord: PropTypes.object,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  hiddenKeys: PropTypes.array,
};

BucketSourceDataRecordDisplayModal.defaultProps = {
  hiddenKeys: ["__id__"],
};

// TODO: Fix ESLint complexity

const BucketDetails = (props) => {
  const { data: defaultBucket } = useGetBucket(props.defaultBucketId);
  const history = useHistory();
  const [bucket, setBucket] = useState(null);
  const chartWidth = props.width;
  const headingRef = useRef(null);
  const [headingHeight, setHeadingHeight] = useState(0);
  const bucketTrendDivRef = useRef(null);
  const [bucketTrendHeight, setBucketTrendHeight] = useState(0);
  const bucketComparisonDivRef = useRef(null);
  const [bucketComparisonHeight, setBucketComparisonHeight] = useState(0);
  const bucketTableDivRef = useRef(null);
  const [bucketTableHeight, setBucketTableHeight] = useState(0);
  const [createBucketModalVisible, setCreateBucketModalVisible] =
    useState(false);
  const [viewBucketsModalVisible, setViewBucketsModalVisible] = useState(false);
  const [recordDetailsModalVisible, setRecordDetailsModalVisible] =
    useState(false);
  const [recordDetailToDisplay, setRecordDetailToDisplay] = useState({});
  const [timeSliceAggregation, setTimeSliceAggregation] = React.useState(
    props.analytic.config.time_slice
      ? props.analytic.config.time_slice.sliceby
      : "",
  );
  const { data: selectedBucket } = useGetBucket(props.defaultBucketId);
  const [selectedLabelString, setSelectedLabelString] = useState("");
  const [bucketToNull, setBucketToNull] = useState(false);
  useEffect(() => {
    setSelectedLabelString(
      JSON.stringify(Array.from(props.selectedLabels).sort()),
    );
  });
  const {
    mutate: updateBucket,
    isError: isUpdateBucketError,
    error: updateBucketError,
    status: updateBucketStatus,
  } = useUpdateBucket();
  useEffect(() => {
    if (isUpdateBucketError) {
      const error = updateBucketError?.response?.data?.detail;
      if (error) {
        toast.error(String(error));
      } else {
        NucToast({
          message: "Error saving Bucket",
          type: "error",
        });
      }
    }
  }, [isUpdateBucketError, updateBucketError]);
  useEffect(() => {
    if (updateBucketStatus === "success") {
      NucToast({
        message: "Bucket saved successfully",
        type: "success",
      });
    }
  }, [updateBucketStatus]);

  useEffect(() => {
    // If the analytic is set, navigate to it
    if (bucket) {
      history.push(
        {
          pathName: `/datasets/${bucket.dataset_id}/stats`,
          search: `?bucketId=${bucket.id}`,
        },
        // `/datasets/${navigateToAnalytic.dataset_id}/stats?bucketId=${navigateToBucketId}`
      );
    }
  }, [bucket]);

  useEffect(() => {
    if (selectedBucket) {
      props.onSelectedLabelsChange(new Set(selectedBucket.selected_labels));
    }
  }, [selectedBucket]);

  const [selectedPocTooltip, setSelectedPocTooltip] = useState(null);

  useEffect(() => {
    if (
      props.selectedLabels && // selected labels exist
      props.analytic
    ) {
      // If there is a selected bucket and it matches the selected labels, set bucket to that
      if (
        selectedBucket && //selectedBucket is set
        selectedBucket.analytic_fk === props.analytic.id && // the same analytic
        selectedLabelString ===
          JSON.stringify([...selectedBucket.selected_labels].sort()) // the selected labels match, sorting doesn't matter)
      ) {
        setBucket(selectedBucket);
      } else if (
        defaultBucket && // default bucket is set
        defaultBucket.analytic_fk === props.analytic.id && // the same analytic
        selectedLabelString ===
          JSON.stringify([...defaultBucket.selected_labels].sort()) // the selected labels match, sorting doesn't matter)
      ) {
        setBucket(defaultBucket);
      } else {
        // Conditional for when we are saving the bucket data. Basically we are just setting the bucket back if the labels change.

        if (
          defaultBucket && // default bucket is set
          defaultBucket.analytic_fk === props.analytic.id && // the same analytic
          selectedLabelString !==
            JSON.stringify([...defaultBucket.selected_labels].sort()) // the selected labels match, sorting doesn't matter)
        ) {
          setBucket(defaultBucket);
        } else if (bucket) {
          setBucket(bucket);
        } else {
          setBucket(null);
        }
      }
    } else {
      setBucket(null);
    }
  }, [props.selectedLabels, props.analytic, defaultBucket, selectedBucket]);

  useEffect(() => {
    setBucket(null);
  }, [bucketToNull]);
  const removeLabel = (label) => {
    // create a new set without the removed label
    let newLabels = [];
    newLabels = Array.from(props.selectedLabels).filter((lbl) => lbl !== label);
    props.onSelectedLabelsChange(new Set(newLabels));
  };

  const colorSelector = d3
    .scaleOrdinal()
    .domain(props.selectedLabels)
    .range(d3.schemeCategory10); //["#e41a1c", "#377eb8", "#4daf4a"]);

  let timeSelections = [];
  if (props.analytic.config.time_slice) {
    if (props.analytic.config.time_slice.sliceby === "split") {
      // This is to support legacy functionality.  Originally, the attribute was named
      // split_date and was a single string.  In v1.6, the ability to select multiple
      // split dates was added.  Old code and configs stored in DB may still reference
      // a single split_date, this needs to be converted into a list of 1
      const splitDates = props.analytic.config.time_slice.split_date
        ? [props.analytic.config.time_slice.split_date]
        : props.analytic.config.time_slice.split_dates;
      //TODO: fix to use last split_by_date
      timeSelections = [
        `${props.analytic.config.time_slice.field.group}.${
          props.analytic.config.time_slice.field.name
        },${props.analytic.config.time_slice.sliceby},${splitDates.join("|")}=${
          props.bucketVisParams.time
        }`,
      ];
    } else {
      timeSelections = [
        `${props.analytic.config.time_slice.field.group}.${props.analytic.config.time_slice.field.name},${props.analytic.config.time_slice.sliceby}=${props.bucketVisParams.time}`,
      ];
    }
  }
  const { data: bucketExampleData, isLoading: isBucketExampleDataLoading } =
    useStatsRecordInfo(
      props.analytic.id,
      {
        bucket_selections: Array.from(props.selectedLabels).map(
          (label) =>
            `prediction.${props.analytic.model_label_class.model_output_name}+=${label}`,
        ),
        slice_selections:
          props.analytic.config.slice_by.length > 0
            ? [
                `${props.analytic.config.slice_by[0].group}.${props.analytic.config.slice_by[0].name}=${props.bucketVisParams.crossSection}`,
              ]
            : [],
        time_selections: timeSelections,
      },
      props.selectedLabels.size === 0,
    );
  const {
    data: upsertedAnalytic,
    mutate: upsertAnalytic,
    isSuccess: isUpsertAnalyticSuccess,
  } = useUpsertAnalytic();
  const [
    newBucketDetailsAnalyticWithTime,
    setNewBucketDetailsAnalyticWithTime,
  ] = useState(null);
  const [newStatData, setNewStatData] = useState(null);
  const {
    data: resStatsData,
    isLoading: newStatsLoading,
    isSuccess: newStatsSuccess,
  } = useGetAnalyticResults(
    newBucketDetailsAnalyticWithTime
      ? newBucketDetailsAnalyticWithTime.id
      : null,
    !!newBucketDetailsAnalyticWithTime,
  );
  useEffect(() => {
    if (newStatsSuccess) {
      setNewStatData(resStatsData);
    }
  }, [newStatsSuccess, resStatsData]);
  useEffect(() => {
    if (isUpsertAnalyticSuccess) {
      setNewBucketDetailsAnalyticWithTime(upsertedAnalytic);
    }
  }, [isUpsertAnalyticSuccess]);
  const upsertAnalyticWithNewTimeAgg = async () => {
    await upsertAnalytic({
      datasetId: props.analytic.dataset_id,
      modelVersionId: props.analytic.model_version_id,
      modelLabelClassId: props.analytic?.model_label_class_id,
      aggregation: props.analytic?.config.aggregation,
      sliceBys: props.analytic?.config.slice_by
        ? props.analytic?.config.slice_by.map((sbc) => ({
            group: "dataset",
            name: sbc.name,
          }))
        : [],
      sliceStats:
        props.analytic?.config.slice_by_stats !== "" &&
        props.analytic?.config.slice_by_columns?.length > 0
          ? [{ name: props.analytic?.config.slice_by_stats }]
          : [],
      sliceProportional: props.analytic?.config.slice_proportional,
      sliceExcludeFromAverage:
        props.analytic?.config.slice_exclude_from_average,
      timeSlice: {
        field: {
          group: props.analytic.config.time_slice?.field.group,
          name: props.analytic.config.time_slice?.field.name,
        },
        sliceby: timeSliceAggregation,
        split_dates: null,
      },
      includeFirstTimePeriod: props.analytic?.config.time_slice_column
        ? props.analytic.config.include_first_time_period
        : true,
      includeLastTimePeriod: props.analytic?.config.time_slice_column
        ? props.analytic.config.include_first_time_period
        : true,
      timeStats:
        props.analytic?.config.time_stats !== "" &&
        props.analytic?.config.time_slice_column
          ? [{ name: props.analytic?.config.time_slice_stats }]
          : [],
      timeProportional: props.analytic?.config.time_proportional,
      dataFilters: props.analytic?.config.filters,
      limitResults: props.analytic?.config.model_params.limit_results,
      resultsLimitedTo:
        props.analytic?.config.limitResults === "aboveprob"
          ? props.analytic?.config.model_params.results_limited_to / 100.0
          : props.analytic?.config.model_params.results_limited_to,
      benchmark:
        props.analytic?.config.benchmark_dataset_id &&
        props.analytic?.config.benchmark_dataset_id !== ""
          ? {
              dataset_id: props.analytic?.config.benchmark_dataset_id,
              label_field: props.analytic?.config.benchmark_label_field,
              benchmark_field: props.analytic?.config.benchmark_field,
            }
          : null,
    });
  };
  const [firstNewStatLoad, setFirstNewStatLoad] = useState(true);
  useEffect(() => {
    if (!firstNewStatLoad) {
      upsertAnalyticWithNewTimeAgg();
    }
    setFirstNewStatLoad(false);
  }, [timeSliceAggregation]);

  const formattedBucketExampleData = bucketExampleData
    ? bucketExampleData.map((d) => {
        // If more than one POC is selected, show which POCs are applicable to the record
        if (props.selectedLabels.size > 1) {
          return {
            __id__: d.id,
            Codes: d.labels,
            ...d.data,
          };
        }
        return {
          __id__: d.id,
          ...d.data,
        };
      })
    : null;

  useLayoutEffect(() => {
    setHeadingHeight(headingRef.current ? headingRef.current.clientHeight : 0);
    setBucketTrendHeight(
      bucketTrendDivRef.current ? bucketTrendDivRef.current.clientHeight : 0,
    );
    setBucketComparisonHeight(
      bucketComparisonDivRef.current
        ? bucketComparisonDivRef.current.clientHeight
        : 0,
    );
    setBucketTableHeight(
      bucketTableDivRef.current ? bucketTableDivRef.current.clientHeight : 0,
    );
  });
  let bucketTrendDifHeight =
    props.bucketVisParams.time && props.bucketVisParams.time !== "" ? 30 : 0;
  let bucketComparisonDifHeight =
    props.bucketVisParams.crossSection !== "" ? 30 : 0;
  let bucketExamplesHeight =
    100 - bucketTrendDifHeight - bucketComparisonDifHeight;
  const topMargin = 10;
  const selectTimeAgg = (
    <CSelect
      style={{
        maxHeight: "30px",
        maxWidth: "60px",
        float: "right",
        fontSize: "x-small",
      }}
      name="timeslice-aggregation-select"
      id="timeslice-aggregation-select"
      onChange={(e) => {
        if (e.target.value === "day") {
          NucToast({
            message: "Day time slice aggregates may be slow.",
            type: "error",
          });
        }
        setTimeSliceAggregation(e.target.value);
      }}
      value={timeSliceAggregation}
    >
      <option value="day">Day</option>
      <option value="week">Week</option>
      <option value="month">Month</option>
      <option value="quarter">Quarter</option>
      <option value="year">Year</option>
    </CSelect>
  );
  const selectBucketButton = (
    <CButton
      color="info"
      size="sm"
      onClick={() => setViewBucketsModalVisible(true)}
      style={{ marginBottom: "2%", minWidth: "-webkit-fill-available" }}
      id="select-bucket-button"
    >
      <span style={{ fontSize: "85%" }}>
        <BsBucketFill style={{ marginRight: "3px" }} />
        Select a Bucket
        <CBadge
          color="secondary"
          style={{
            marginLeft: "5px",
            fontSize: "75%",
          }}
        >
          <i>Preview</i>
        </CBadge>
      </span>
    </CButton>
  );
  // const showInformation = (message = "") => {
  //   toast.success(message, {
  //     style: {
  //       border: "1px solid #132E31",
  //       padding: "16px",
  //       color: "#132e31",
  //       backgroundColor: "#e6f2f3",
  //     },
  //     iconTheme: {
  //       primary: "#132e31",
  //       secondary: "#132e31",
  //       hidden: true,
  //     },
  //     duration: 7000,
  //     position: "bottom-center",
  //   });
  // };
  const renderSaveBucketButton = () => {
    if (defaultBucket || selectedBucket || bucket) {
      // Condition for when we are rerendering and defaultBucket changes
      if (defaultBucket && defaultBucket.id !== bucket?.id) {
        return false;
      }
      // Condition for if labels change
      // case for when existing buckets come through and have no config set
      if (
        defaultBucket &&
        !defaultBucket.config &&
        defaultBucket.id === bucket?.id
      ) {
        return true;
      }

      if (
        bucket &&
        selectedLabelString !==
          JSON.stringify(Array.from(bucket.selected_labels).sort())
      ) {
        return true;
      }
      if (defaultBucket) {
        if (
          defaultBucket.config.selected_cross_section !==
          props.bucketVisParams.crossSection
        ) {
          return true;
        }
        if (defaultBucket.config.time !== props.bucketVisParams.time) {
          return true;
        }
      }
    }
    return false;
  };
  const saveBucketButton = (
    <CButton
      style={{ minWidth: "-webkit-fill-available" }}
      color="warning"
      size="sm"
      id="save-bucket-button"
      onClick={() => {
        let bucketId = defaultBucket ? defaultBucket.id : bucket.id;
        let bucketParam = defaultBucket ? defaultBucket : bucket;
        let crossSection = props?.bucketVisParams?.crossSection;
        let time = props?.bucketVisParams?.time;
        let config = {};
        config = {
          selected_cross_section: crossSection,
          time: time,
        };
        updateBucket({
          bucketId,
          bucket: {
            name: bucketParam.name,
            analytic_fk: bucketParam.analytic_fk,
            config: config,
            description: bucketParam.description,
            allow_others_to_edit: bucketParam.allow_others_to_edit,
            selected_labels: [...props.selectedLabels],
          },
        });
      }}
    >
      <span style={{ fontSize: "85%" }}>
        <BsBucketFill style={{ marginRight: "3px" }} />
        Save a Bucket
        <CBadge
          color="secondary"
          style={{
            marginLeft: "5px",
            fontSize: "75%",
          }}
        >
          <i>Preview</i>
        </CBadge>
      </span>
    </CButton>
  );
  const selectBucketModal = (
    <BucketListModal
      analyticId={props.analytic.id}
      bucketVisParams={props.bucketVisParams}
      isVisible={viewBucketsModalVisible}
      onClose={() => setViewBucketsModalVisible(false)}
      setBucketToNull={setBucketToNull}
    />
  );
  const selectedTooltipDataPoint = props.data?.filter(
    (d) => d.Label === selectedPocTooltip?.label,
  )[0];
  return (
    <div style={{ height: props.height - topMargin }}>
      <CContainer style={{ maxWidth: "9999px", padding: "0", margin: "0" }}>
        <div
          ref={headingRef}
          style={{ margin: `${topMargin}px 10px 0px 10px` }}
        >
          <CRow>
            {!props.selectedLabels || props.selectedLabels.size === 0 ? (
              <>
                <CCol xs="7">
                  <p>
                    <i>
                      Select one or more points on the chart to view additional
                      details
                    </i>
                  </p>
                </CCol>
                {selectTimeAgg}
                <CCol>
                  <div style={{ width: "60px" }}>{selectBucketButton}</div>
                  {selectBucketModal}
                </CCol>
              </>
            ) : (
              <>
                <Toaster
                  toastOptions={{
                    style: {
                      marginTop: "30px",
                    },
                    duration: 1700,
                  }}
                />
                <CCol xs="7">
                  {bucket && (
                    <h5 id="bucket-name-h5" style={{ marginBottom: "5px" }}>
                      <BsBucketFill style={{ marginRight: "5px" }} />
                      <b>{bucket.name}</b>
                    </h5>
                  )}
                  <h5>
                    {props.selectedLabels.map((label, idx) => {
                      return (
                        <CBadge
                          key={idx}
                          color="primary"
                          style={{
                            marginLeft: "5px",
                            backgroundColor: colorSelector(label),
                          }}
                        >
                          <span
                            style={{
                              cursor: "default",
                            }}
                            onMouseOver={(e) => {
                              setSelectedPocTooltip({
                                label: label,
                                clientX: e.clientX,
                                clientY: e.clientY,
                              });
                            }}
                            onMouseLeave={() => {
                              setSelectedPocTooltip(null);
                            }}
                          >
                            {label}
                          </span>
                          <span
                            style={{
                              cursor: "pointer",
                              marginLeft: "10px",
                              fontSize: "75%",
                              color: "#BBBBBB",
                            }}
                            onClick={() => removeLabel(label)}
                          >
                            x
                          </span>
                        </CBadge>
                      );
                    })}
                  </h5>
                  <div
                    style={{
                      visibility: selectedPocTooltip ? "visible" : "hidden",
                      position: "fixed",
                      left:
                        (selectedPocTooltip?.clientX
                          ? selectedPocTooltip?.clientX
                          : 0) + 20,
                      top: selectedPocTooltip?.clientY,
                      zIndex: 10000,
                      backgroundColor: "#FFF",
                      border: "1px solid black",
                      borderRadius: "5px",
                      padding: "10px",
                      fontSize: "85%",
                    }}
                  >
                    {selectedTooltipDataPoint && (
                      <LabelToolTip
                        dataPoint={selectedTooltipDataPoint}
                        bucketVisParams={props.bucketVisParams}
                      />
                    )}
                  </div>
                </CCol>
                <CCol xs="5" style={{ textAlign: "right" }}>
                  <CButtonGroup>
                    {selectTimeAgg}
                    <CCol>
                      {(!bucket ||
                        JSON.stringify(
                          Array.from(bucket.selected_labels).sort(),
                        ) !==
                          JSON.stringify(
                            Array.from(props.selectedLabels).sort(),
                          )) && (
                        <CButton
                          color="success"
                          id="create-bucket-button"
                          size="sm"
                          onClick={() => setCreateBucketModalVisible(true)}
                          style={{
                            marginBottom: "2%",
                            minWidth: "-webkit-fill-available",
                          }}
                        >
                          <span style={{ fontSize: "85%" }}>
                            <BsBucketFill style={{ marginRight: "3px" }} />
                            Create a Bucket
                            <CBadge
                              color="secondary"
                              style={{
                                marginLeft: "5px",
                                fontSize: "75%",
                              }}
                            >
                              <i>Preview</i>
                            </CBadge>
                          </span>
                        </CButton>
                      )}
                      {selectBucketButton}
                      {renderSaveBucketButton() ? saveBucketButton : <></>}
                    </CCol>
                  </CButtonGroup>
                  <CreateBucketModal
                    analyticId={props.analytic.id}
                    selectedCrossSection={props.bucketVisParams.crossSection}
                    selectedLabels={Array.from(props.selectedLabels)}
                    isVisible={createBucketModalVisible}
                    onClose={() => setCreateBucketModalVisible(false)}
                    onBucketCreate={(createBucket) => {
                      if (
                        !bucket ||
                        (createBucket && createBucket.id !== bucket.id)
                      )
                        setBucket(createBucket);
                    }}
                    config={props.bucketVisParams}
                  />
                  {selectBucketModal}
                </CCol>
              </>
            )}
          </CRow>
        </div>
        <div style={{ height: props.height - headingHeight - topMargin }}>
          {props.selectedLabels && props.selectedLabels.size > 0 && (
            <>
              {bucketTrendDifHeight > 0 && (
                <div
                  ref={bucketTrendDivRef}
                  style={{ height: bucketTrendDifHeight + "%" }}
                >
                  <BucketTrendChart
                    width={chartWidth}
                    height={bucketTrendHeight}
                    // change to our new analytic
                    analytic={
                      upsertedAnalytic ? upsertedAnalytic : props.analytic
                    }
                    newStatLoading={newStatsLoading}
                    // Our new stats data needs to get populated here
                    statsData={newStatData ? newStatData : props.statsData}
                    // statsData={props.statsData}
                    selectedLabels={props.selectedLabels}
                    bucketVisParams={props.bucketVisParams}
                    colorSelector={colorSelector}
                  />
                </div>
              )}
              {bucketComparisonDifHeight > 0 && (
                <div
                  ref={bucketComparisonDivRef}
                  style={{ height: bucketComparisonDifHeight + "%" }}
                >
                  <BucketComparisonChart
                    width={chartWidth}
                    height={bucketComparisonHeight}
                    analytic={
                      upsertedAnalytic ? upsertedAnalytic : props.analytic
                    }
                    newStatLoading={newStatsLoading}
                    statsData={newStatData ? newStatData : props.statsData}
                    selectedLabels={props.selectedLabels}
                    bucketVisParams={props.bucketVisParams}
                    colorSelector={colorSelector}
                  />
                </div>
              )}
              <div
                ref={bucketTableDivRef}
                style={{ height: bucketExamplesHeight + "%" }}
              >
                {isBucketExampleDataLoading ? (
                  <div
                    style={{
                      textAlign: "center",
                      paddingTop: bucketTableHeight * 0.35,
                    }}
                  >
                    <Loading
                      height={bucketTableHeight * 0.3}
                      width={bucketTableHeight * 0.3}
                    />
                  </div>
                ) : (
                  formattedBucketExampleData && (
                    <>
                      <DatasetModelStatsResultsTable
                        height={bucketTableHeight + "px"}
                        width={chartWidth}
                        // This needs updated with our new time data
                        results={formattedBucketExampleData}
                        scale={0.75}
                        onRowClick={(d) => {
                          setRecordDetailToDisplay(d.row);
                          setRecordDetailsModalVisible(true);
                        }}
                      />
                      <BucketSourceDataRecordDisplayModal
                        displayRecord={recordDetailToDisplay}
                        isVisible={recordDetailsModalVisible}
                        onClose={() => {
                          setRecordDetailsModalVisible(false);
                        }}
                      />
                    </>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </CContainer>
    </div>
  );
};

BucketDetails.propTypes = {
  setNewBucketIdInUrl: PropTypes.func,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  analytic: PropTypes.object.isRequired,
  defaultBucketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.array.isRequired,
  statsData: PropTypes.array.isRequired,
  selectedLabels: PropTypes.instanceOf(Set),
  onSelectedLabelsChange: PropTypes.func.isRequired,
  bucketVisParams: PropTypes.object.isRequired,
  retrigger: PropTypes.bool,
};

const BucketFilterNumberComponent = (props) => {
  return (
    <input
      type="number"
      autoFocus={true}
      onKeyDownCapture={(e) => {
        // if empty and backspace pressed, reenable filter type and delete last character
        if (e.key === "Backspace" && props.inputValue === "") {
          props.setInputFilterType(props.inputFilterType.slice(0, -1) + " ");
        } else if (e.key === "Enter") {
          props.addToExclusionList([
            `${props.inputFilterType}: ${props.inputValue}`,
            props.inputValue,
          ]);
          props.setInputFilterType("");
          props.setInputValue("");
          props.setInputEnabled(false);
        }
      }}
      value={props.inputValue}
      onChange={(e) => props.setInputValue(e.target.value)}
    />
  );
};

BucketFilterNumberComponent.propTypes = {
  inputValue: PropTypes.string.isRequired,
  setInputValue: PropTypes.func.isRequired,
  inputFilterType: PropTypes.string.isRequired,
  setInputFilterType: PropTypes.func.isRequired,
  addToExclusionList: PropTypes.func.isRequired,
  setInputEnabled: PropTypes.func.isRequired,
};

export const ExclusionSelector = ({ analyticId, onExclusionChange }) => {
  /* Component for creating and maintaining a list of poc and criteria to exclude from the visualization */
  // Possible types of exclusion filters
  const filterTypeOptions = [
    "Bucket",
    "Count/Sum >",
    "Count/Sum <",
    "POC",
    "Site Over Site Change >",
    "Period Over Period Change >",
    "Industry Compare Change >",
  ];
  // List of all exclusions.  Each item should be the filter text as the first element, and any
  // supporting data as the second element, e.g. ["Bucket: ABC", {name: "Bucket: ABC", selectedLabels: ...}]
  const [exclusionList, setExclusionList] = useState([]);
  //When the exclusion list is updated, calculate the excluded POC codes and return
  useEffect(() => {
    let excludedPocs = new Set();
    let pocFilters = new Set();

    exclusionList.map((exclusion) => {
      const [key, data] = exclusion;
      switch (true) {
        case key?.startsWith("Bucket"):
          data?.selected_labels?.map((label) => {
            excludedPocs.add(label);
          });
          break;
        case key?.startsWith("POC"):
          excludedPocs.add(data);
          break;
        case key?.startsWith("Count/Sum >"):
          pocFilters.add(["Count/Sum >", data]);
          break;
        case key?.startsWith("Count/Sum <"):
          pocFilters.add(["Count/Sum <", data]);
          break;
        case key?.startsWith("Site Over Site Change >"):
          pocFilters.add(["Site Over Site Change >", data]);
          break;
        case key?.startsWith("Period Over Period Change >"):
          pocFilters.add(["Period Over Period Change >", data]);
          break;
        case key?.startsWith("Industry Compare Change >"):
          pocFilters.add(["Industry Compare Change >", data]);
          break;
        default:
          console.log(
            "This shouldn't happen, can't process this element",
            exclusion,
          );
      }
    });
    // Call the onExclusionChange func passed via props.  Convert the set objects to
    // arrays.  This ensures the values are returned instead of a object reference,
    // which makes upstream state management a little more straight forward
    onExclusionChange({
      excludedPocs: Array.from(excludedPocs),
      pocFilters: Array.from(pocFilters),
    });
  }, [exclusionList]);

  // Function for adding a new item to the exclusion list.  If the first element in the
  // new item matches an existing entry, don't add it!
  const addToExclusionList = (newItem) => {
    if (!exclusionList.find((item) => item[0] === newItem[0])) {
      let newExclusionList = [...exclusionList];
      newExclusionList.push(newItem);
      setExclusionList(newExclusionList);
    }
  };
  // Whether new input is being accepted
  const [inputEnabled, setInputEnabled] = useState(false);
  // The type of filter being built
  const [inputFilterType, setInputFilterType] = useState("");
  // The value of the filter
  const [inputValue, setInputValue] = useState("");
  // Whether a complete inputFilterType has been typed in
  const inputFilterTypeIsSet = filterTypeOptions.includes(inputFilterType);
  // List of all buckets for this analytic
  const { data: bucketList } = useBucketList({
    analyticId: analyticId,
    details: false,
  });
  // List of all WANO POC codes
  const { data: labelMap } = useLabelMap("wano");
  // Helper function.  First applies the "selecter" function on values a and b,
  // then compares the lowercase values of the a and b.
  const lowerCaseSort = (a, b, selecter) => {
    const aL = (selecter ? selecter(a) : a)?.toLowerCase();
    const bL = (selecter ? selecter(b) : b)?.toLowerCase();
    if (aL > bL) {
      return 1;
    } else if (aL < bL) {
      return -1;
    }
    return 0;
  };
  // If the inputFilterType is bucket, filter the list down to just those matching
  // the value typed so far, and sort alphabetically.
  const sortedFilteredBucketList =
    bucketList && inputFilterType === "Bucket"
      ? bucketList
          .filter((item) => {
            return item.name
              .toLowerCase()
              .startsWith(inputValue?.toLowerCase());
          })
          .sort((a, b) => {
            return lowerCaseSort(a, b, (x) => x?.name);
          })
      : [];
  // Adding all to sortedFilteredBucketList
  sortedFilteredBucketList.unshift({ name: "- All Buckets -" });
  // If the inputFilterType is POC, filter the POC list down to just those matching
  // the value typed so far, and sort alphabetically.
  const sortedFilteredPOCList =
    labelMap && inputFilterType === "POC"
      ? Object.keys(labelMap)
          .filter((key) => {
            return key.toLowerCase().startsWith(inputValue?.toLowerCase());
          })
          .sort(lowerCaseSort)
      : [];

  return (
    <div style={{ display: "flex", alignItems: "center", padding: "5px" }}>
      {/* Header */}
      <div style={{ float: "left", marginRight: "10px" }}>
        <h6>
          <CBadge
            color="secondary"
            style={{
              marginLeft: "5px",
              marginRight: "5px",
              fontSize: "75%",
            }}
          >
            <i>Preview</i>
          </CBadge>
          Exclude items from visualization:
        </h6>
      </div>
      {/* Create a badge for every exclusion list item */}
      {exclusionList.map((item, idx) => {
        return (
          <div key={idx} style={{ float: "left" }}>
            <CBadge
              color="primary"
              style={{
                verticalAlign: "top",
                fontSize: "90%",
                marginRight: "5px",
                height: "40px",
                backgroundColor: item[0].startsWith("Bucket")
                  ? "#66F"
                  : item[0].startsWith("POC")
                    ? "#2AA"
                    : "#A2A",
              }}
            >
              {item[0]}
              <span
                style={{
                  cursor: "pointer",
                  marginLeft: "10px",
                  fontSize: "100%",
                  color: "#BBBBBB",
                }}
                onClick={() => {
                  // Remove itself from the list
                  const newExclusionList = exclusionList
                    .slice(0, idx)
                    .concat(exclusionList.slice(idx + 1));
                  setExclusionList(newExclusionList);
                }}
              >
                x
              </span>
            </CBadge>
          </div>
        );
      })}
      {/* This div contains the input widget for creating new exclusion items */}
      <div
        style={{
          float: "left",
          marginBottom: "10px",
          padding: "0",
        }}
      >
        {inputEnabled ? (
          // When inputEnabled, show exclusion input widget
          <>
            {!inputFilterTypeIsSet && (
              // If the inputFilterType hasn't been set yet, display autocomplete input for it
              <Autocomplete
                inputProps={{
                  autoFocus: true,
                  onKeyDown: (e) => {
                    // if empty and backspace pressed, delete
                    if (e.key === "Backspace" && inputFilterType === "") {
                      setInputEnabled(false);
                    }
                  },
                }}
                autoHighlight={true}
                getItemValue={(item) => item || ""}
                items={filterTypeOptions}
                renderItem={(item, isHighlighted) => (
                  <div
                    style={{
                      background: isHighlighted ? "lightgray" : "white",
                    }}
                    key={item}
                  >
                    {item}
                  </div>
                )}
                value={inputFilterType ? inputFilterType : ""}
                onChange={(e) => setInputFilterType(e.target.value)}
                onSelect={(val) => setInputFilterType(val)}
              />
            )}
            {inputFilterTypeIsSet && (
              // After the inputFilterType is set, get the value input
              <>
                {`${inputFilterType}:`}
                {inputFilterType === "Bucket" && (
                  // value input when bucket exclusion
                  <Autocomplete
                    inputProps={{
                      menuStyle: { position: "fixed" },
                      autoFocus: true,
                      onKeyDownCapture: (e) => {
                        // if empty and backspace pressed, reenable filter type and delete last character
                        if (e.key === "Backspace" && inputValue === "") {
                          setInputFilterType(
                            inputFilterType.slice(0, -1) + " ",
                          );
                        }
                      },
                    }}
                    getItemValue={(item) => item?.name || ""}
                    items={sortedFilteredBucketList}
                    renderItem={(item, isHighlighted) => (
                      <div
                        key={item.name}
                        style={{
                          background: isHighlighted ? "lightgray" : "white",
                        }}
                      >
                        {item.name}
                      </div>
                    )}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onSelect={(val, item) => {
                      // If all buckets, iterate sortedBucketList and add all buckets to exclusion list
                      if (val === "- All Buckets -") {
                        const newExclusionList = sortedFilteredBucketList
                          .slice(1)

                          .map((item) => [`Bucket: ${item.name}`, item]);
                        setExclusionList(
                          exclusionList.concat(newExclusionList),
                        );
                        setInputFilterType("");
                        setInputValue("");
                        setInputEnabled(false);
                      } else {
                        addToExclusionList([
                          `${inputFilterType}: ${val}`,
                          item,
                        ]);
                        setInputFilterType("");
                        setInputValue("");
                        setInputEnabled(false);
                      }
                    }}
                  />
                )}
                {inputFilterType === "POC" && (
                  // value input when POC exclusion
                  <Autocomplete
                    inputProps={{
                      menuStyle: { position: "fixed" },
                      autoFocus: true,
                      onKeyDownCapture: (e) => {
                        // if empty and backspace pressed, reenable filter type and delete last character
                        if (e.key === "Backspace" && inputValue === "") {
                          setInputFilterType(
                            inputFilterType.slice(0, -1) + " ",
                          );
                        }
                      },
                    }}
                    getItemValue={(item) => item || ""}
                    items={sortedFilteredPOCList}
                    renderItem={(item, isHighlighted) => (
                      <div
                        key={item}
                        style={{
                          background: isHighlighted ? "lightgray" : "white",
                        }}
                      >
                        {item}
                      </div>
                    )}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onSelect={(val) => {
                      addToExclusionList([`${inputFilterType}: ${val}`, val]);
                      setInputFilterType("");
                      setInputValue("");
                      setInputEnabled(false);
                    }}
                  />
                )}
                {inputFilterType === "Count/Sum >" && (
                  // value input when Count/Sum > exclusion
                  <BucketFilterNumberComponent
                    inputValue={inputValue}
                    inputFilterType={inputFilterType}
                    setInputValue={setInputValue}
                    addToExclusionList={addToExclusionList}
                    setInputFilterType={setInputFilterType}
                    setInputEnabled={setInputEnabled}
                  />
                )}
                {inputFilterType === "Count/Sum <" && (
                  // value input when Count/Sum < exclusion
                  <BucketFilterNumberComponent
                    inputValue={inputValue}
                    inputFilterType={inputFilterType}
                    setInputValue={setInputValue}
                    addToExclusionList={addToExclusionList}
                    setInputFilterType={setInputFilterType}
                    setInputEnabled={setInputEnabled}
                  />
                )}
                {inputFilterType === "Site Over Site Change >" && (
                  // value input when Site Over Site Change > exclusion
                  <BucketFilterNumberComponent
                    inputValue={inputValue}
                    inputFilterType={inputFilterType}
                    setInputValue={setInputValue}
                    addToExclusionList={addToExclusionList}
                    setInputFilterType={setInputFilterType}
                    setInputEnabled={setInputEnabled}
                  />
                )}
                {inputFilterType === "Industry Compare Change >" && (
                  // value input when Industry Compare Change >
                  <BucketFilterNumberComponent
                    inputValue={inputValue}
                    inputFilterType={inputFilterType}
                    setInputValue={setInputValue}
                    addToExclusionList={addToExclusionList}
                    setInputFilterType={setInputFilterType}
                    setInputEnabled={setInputEnabled}
                  />
                )}
                {inputFilterType === "Period Over Period Change >" && (
                  // value input when Period Over Period Change >
                  <BucketFilterNumberComponent
                    inputValue={inputValue}
                    inputFilterType={inputFilterType}
                    setInputValue={setInputValue}
                    addToExclusionList={addToExclusionList}
                    setInputFilterType={setInputFilterType}
                    setInputEnabled={setInputEnabled}
                  />
                )}
              </>
            )}
          </>
        ) : (
          // Button to add another exclusion
          <CBadge
            size="sm"
            color="secondary"
            style={{
              cursor: "pointer",
              padding: "2px",
              height: "20px",
              verticalAlign: "top",
            }}
            onClick={() => {
              setInputEnabled(true);
            }}
          >
            <CIcon content={freeSet.cilPlus} />
          </CBadge>
        )}
      </div>
    </div>
  );
};

ExclusionSelector.propTypes = {
  analyticId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onExclusionChange: PropTypes.func,
};

ExclusionSelector.defaultProps = {
  onExclusionChange: () => {},
};

export const BucketVisPackage = (props) => {
  const chartDivRef = useRef(null);
  const [chartDivWidth, setChartDivWidth] = useState(0);
  const detailsDivRef = useRef(null);
  const [detailsDivWidth, setDetailsDivWidth] = useState(0);
  const [bucketVisParams, setBucketVisParams] = useState(null);
  const [visData, setVisData] = useState(null);
  const [visDataStats, setVisDataStats] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState(new Set());
  const [exclusionData, setExclusionData] = useState({
    excludedPocs: [],
    pocFilters: [],
  });

  const { data: analytic } = useGetAnalytic(props.analyticId);
  const { data: defaultBucket } = useGetBucket(
    parseInt(props.defaultBucketId, 10),
  );
  useEffect(() => {
    if (
      analytic &&
      defaultBucket &&
      analytic.id === defaultBucket.analytic_fk
    ) {
      // default bucket is selected, and analytic matches, default selected labels
      setSelectedLabels(new Set(defaultBucket.selected_labels));
    }
  }, [analytic, defaultBucket]);

  const { data: labelMapData } = useLabelMap("wano");
  useLayoutEffect(() => {
    setChartDivWidth(chartDivRef.current ? chartDivRef.current.clientWidth : 0);
    setDetailsDivWidth(
      detailsDivRef.current ? detailsDivRef.current.clientWidth : 0,
    );
  });
  // TODO: fix eslint complexity warning

  useEffect(() => {
    // This effect builds the visual data for the cluster
    let newErrorMessages = [];
    if (
      props.statsData &&
      analytic &&
      bucketVisParams &&
      labelMapData &&
      typeof labelMapData !== "undefined"
    ) {
      // only build the visual data if all the required data is present
      const { bucketColumn, sliceByColumn, timeSliceColumn, benchmarkColumn } =
        calcResultColumns(analytic);
      try {
        let newVisData = [];
        let seenLabels = new Set();
        // TODO: add PO&C codes that have no data
        for (let i = 0; i < props.statsData.length; i++) {
          const d = props.statsData[i];
          let isSelectedSection = true;
          if (sliceByColumn && timeSliceColumn) {
            isSelectedSection =
              d[sliceByColumn] === bucketVisParams.crossSection &&
              d[timeSliceColumn] === bucketVisParams.time;
          } else if (sliceByColumn) {
            isSelectedSection =
              d[sliceByColumn] === bucketVisParams.crossSection;
          } else if (timeSliceColumn) {
            isSelectedSection = d[timeSliceColumn] === bucketVisParams.time;
          }
          if (isSelectedSection) {
            // check if the slice and time match the selected values
            if (!(bucketColumn in d)) {
              throw "Bucket column not found in data.";
            }
            const label = d[bucketColumn];
            seenLabels.add(label);
            const count = "Count" in d ? d.Count : d["Sum of Probabilities"]; // size of the data points
            if (!(label in labelMapData)) {
              //throw "Unknown Wano label: " + label;
            } else {
              const labelData = labelMapData[label];
              let heat = 0; // Color of the data points
              let heatDenom = 0;
              let heatContributors = {};
              if (sliceByColumn) {
                heatContributors[PCT_DIFF_FROM_AVG_SLICE_TEXT] =
                  d["Relative Difference from Avg (across slice)"];
                heat +=
                  d["Relative Difference from Avg (across slice)"] *
                  bucketVisParams.heatWeights.slice;
                heatDenom += bucketVisParams.heatWeights.slice;
              }
              // Filter on this time slice also in heat contributor
              if (timeSliceColumn) {
                heatContributors[PCT_DIFF_FROM_AVG_TIME_TEXT] =
                  d["Relative Difference from Avg (across time slice)"];
                heat +=
                  d["Relative Difference from Avg (across time slice)"] *
                  bucketVisParams.heatWeights.time;
                heatDenom += bucketVisParams.heatWeights.time;
              }
              // Filter on this benchmark and it's in heat contributor
              if (benchmarkColumn) {
                const pctDiffBenchmark =
                  d["Relative Difference from Benchmark"];
                heatContributors[PCT_DIFF_FROM_BENCHMARK_TEXT] =
                  pctDiffBenchmark;
                heat +=
                  pctDiffBenchmark * bucketVisParams.heatWeights.benchmark;
                heatDenom += bucketVisParams.heatWeights.benchmark;
              }
              if (heatDenom > 0) {
                heat /= heatDenom;
              }
              //d["Percent of Records w/ Prediction within Group"]; // size of the data points
              newVisData.push({
                Label: d[bucketColumn],
                Description: labelData.Description,
                umap_x: labelData.umap_x,
                umap_y: labelData.umap_y,
                heat: heat,
                count: count,
                proportion: d["Percent of Records w/ Prediction within Group"],
                benchmarkProportion:
                  d["Benchmark Percent of Records w/ Prediction"],
                heatContributors: heatContributors,
              });
            }
          }
        }
        for (const [key, value] of Object.entries(labelMapData)) {
          // add all of the wano codes that weren't in the results dataset
          if (!seenLabels.has(key)) {
            newVisData.push({
              Label: key,
              Description: value.Description,
              umap_x: value.umap_x,
              umap_y: value.umap_y,
              heat: 0.0,
              count: 0.0,
              proportion: 0.0,
              benchmarkProportion: undefined,
              heatContributors: {},
            });
          }
        }
        newVisData.sort(function (a, b) {
          return a.count - b.count;
        });
        newVisData.reverse();
        setVisDataStats(calcStats(newVisData));
        let excludedPocsSet = new Set(exclusionData.excludedPocs);
        // apply exclusion filters
        //TODO: improve filter performance by only iterating over newVisData once
        newVisData = newVisData.filter((d) => !excludedPocsSet.has(d.Label));

        exclusionData.pocFilters.map((pocFilter) => {
          const [pocFilterType, pocFilterValue] = pocFilter;
          if (pocFilterType === "Count/Sum >") {
            newVisData = newVisData.filter((d) => d.count > pocFilterValue);
          } else if (pocFilterType === "Count/Sum <") {
            newVisData = newVisData.filter((d) => d.count < pocFilterValue);
            // Move most things below this to some other function that iterates the newVisData
          } else if (pocFilterType === "Site Over Site Change >") {
            // Time slice site
            newVisData = newVisData.filter(
              (d) =>
                d.heatContributors["% Difference from average - Slice"] >
                pocFilterValue,
            );
          } else if (pocFilterType === "Period Over Period Change >") {
            newVisData = newVisData.filter(
              (d) =>
                d.heatContributors["% Difference from average - Time"] >
                pocFilterValue,
            );
          } else if (pocFilterType === "Industry Compare Change >") {
            newVisData = newVisData.filter(
              (d) =>
                d.heatContributors["% Difference from Benchmark"] >
                pocFilterValue,
            );
          } else {
            console.log("Unknown poc filter type", pocFilterType);
          }
        });
        setVisData(newVisData);
      } catch (error) {
        newErrorMessages.push(String(error));
      }
    } else {
      setVisData(null);
      setVisDataStats(null);
    }
    setErrorMessages(newErrorMessages);
  }, [
    bucketVisParams,
    props,
    analytic,
    labelMapData,
    exclusionData,
    props.defaultBucketId,
  ]);

  if (errorMessages.length > 0) {
    return <ErrorMessageAlerts errorMessages={errorMessages} />;
  }
  return (
    <>
      {analytic && (
        <div>
          <CCardHeader style={{ paddingBottom: "0px" }}>
            <BucketVisOptions
              analytic={analytic}
              statsData={props.statsData}
              defaultBucketId={props.defaultBucketId}
              onParamsChange={setBucketVisParams}
            />
          </CCardHeader>
          <CCardBody
            style={{
              padding: "0",
            }}
          >
            {visData && visDataStats && (
              <CContainer style={{ maxWidth: "9999px" }}>
                <CRow>
                  <CCol xs="7" sm="7" md="7" style={{ padding: "0" }}>
                    <div ref={chartDivRef} style={{ width: "100%" }}>
                      <BucketVisualization
                        data={visData}
                        dataStats={visDataStats}
                        height={0.9 * (window.innerHeight - 100)}
                        width={chartDivWidth}
                        selectedLabels={selectedLabels}
                        onSelectedLabelsChange={setSelectedLabels}
                        bucketVisParams={bucketVisParams}
                        defaultBucketId={props.defaultBucketId}
                      />
                    </div>
                  </CCol>
                  <CCol xs="5" sm="5" md="5" style={{ padding: "0" }}>
                    <div ref={detailsDivRef} style={{ width: "100%" }}>
                      <BucketDetails
                        retrigger={false}
                        height={0.9 * (window.innerHeight - 100)}
                        width={detailsDivWidth}
                        selectedLabels={selectedLabels}
                        analytic={analytic}
                        defaultBucketId={props.defaultBucketId}
                        data={visData}
                        statsData={props.statsData}
                        onSelectedLabelsChange={setSelectedLabels}
                        bucketVisParams={bucketVisParams}
                        setNewBucketIdInUrl={props.setNewBucketIdInUrl}
                      />
                    </div>
                  </CCol>
                  {/* Padding Bottom 75px to prevent bucket vis filter options for going off the page. */}
                  <CCol
                    xs="12"
                    sm="12"
                    md="12"
                    style={{ padding: "0", paddingBottom: "75px" }}
                  >
                    <div style={{ width: "100%" }}>
                      <ExclusionSelector
                        analyticId={props.analyticId}
                        onExclusionChange={(data) => {
                          setExclusionData(data);
                        }}
                      />
                    </div>
                  </CCol>
                </CRow>
              </CContainer>
            )}
          </CCardBody>
        </div>
      )}
    </>
  );
};

BucketVisPackage.propTypes = {
  analyticId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  defaultBucketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  statsData: PropTypes.array.isRequired,
  setNewBucketIdInUrl: PropTypes.func,
  setBucketToNull: PropTypes.func,
  selectedLabels: PropTypes.instanceOf(Set) || PropTypes.array,
};

export default BucketVisualization;
